import { countBy, min } from 'lodash';

import { CardSetAffinities } from '@mythos/game/CardTypes';
import { CardDef } from '@mythos/game/GameModel';
import { CardWithID } from '@mythos/game/Rules';
import {
  MERCENARY_CARD_DEF_IDS,
  SCAVENGE_CARD_DEF_IDS,
  SETTLEMENT_CARD_DEF_IDS,
} from './GameConstants';

const VERBOSE = false;

export function countBoardSets(board: CardWithID[]): number {
  var counts = countBy(board, (card) => {
    return card.affinity;
  });
  return (
    min(
      CardSetAffinities.map((type) => {
        return counts[type] || 0;
      }),
    ) ?? 0
  );
}

export interface CounterDelta {
  gold: number;
  favor: number;
  military: number;
  warTokens: number;
  tributeTokens: number;
}
export function makeCounterDelta(): CounterDelta {
  return {
    gold: 0,
    favor: 0,
    military: 0,
    warTokens: 0,
    tributeTokens: 0,
  };
}

export function applyCounterDelta(
  target: CounterDelta,
  delta: Partial<CounterDelta>,
): CounterDelta {
  target.gold += delta.gold || 0;
  target.military += delta.military || 0;
  target.favor += delta.favor || 0;
  target.warTokens += delta.warTokens || 0;
  target.tributeTokens += delta.tributeTokens || 0;
  return target;
}

export function addCounters(
  counters: CounterDelta,
  other: Partial<CounterDelta>,
): CounterDelta {
  const output = { ...counters };
  return applyCounterDelta(output, other);
}

export function sumCounters(counters: Partial<CounterDelta>[]): CounterDelta {
  return counters.reduce<CounterDelta>((acc, curr) => {
    return applyCounterDelta(acc, curr);
  }, makeCounterDelta());
}

export function multiplyCounters(
  counters: CounterDelta,
  factor: number,
): CounterDelta {
  const output = { ...counters };
  output.gold *= factor;
  output.military *= factor;
  output.favor *= factor;
  output.warTokens *= factor;
  output.tributeTokens *= factor;
  return output;
}

export function subtractCounterDeltas(
  delta1: CounterDelta,
  delta2: CounterDelta,
): CounterDelta {
  const output = { ...delta1 };
  output.gold -= delta2.gold;
  output.military -= delta2.military;
  output.favor -= delta2.favor;
  output.warTokens -= delta2.warTokens;
  output.tributeTokens -= delta2.tributeTokens;
  return output;
}

export function compressCounterDelta(
  delta: CounterDelta,
): Partial<CounterDelta> {
  const output: Partial<CounterDelta> = {};
  for (const key of Object.keys(delta) as (keyof CounterDelta)[]) {
    if (delta[key] !== 0) {
      output[key] = delta[key];
    }
  }
  return output;
}

export function isZeroCounterDelta(counters: Partial<CounterDelta>): boolean {
  return (
    (counters.gold ?? 0) === 0 &&
    (counters.military ?? 0) === 0 &&
    (counters.favor ?? 0) === 0 &&
    (counters.warTokens ?? 0) === 0 &&
    (counters.tributeTokens ?? 0) === 0
  );
}

export function countIf<T>(list: Iterable<T>, iterator: (v: T) => boolean) {
  let count = 0;
  for (const v of list) {
    if (iterator(v)) {
      count++;
    }
  }
  return count;
}

export function sum<T>(list: Iterable<T>, iterator: (v: T) => number) {
  let sum = 0;
  for (const v of list) {
    sum += iterator(v);
  }
  return sum;
}

export function ageString(age: number): string {
  return 'I'.repeat(age);
}

export function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

export function cumsum(list: number[]): number[] {
  return list.reduce((acc, curr) => {
    acc.push((acc[acc.length - 1] || 0) + curr);
    return acc;
  }, [] as number[]);
}

export function debug_log(...content: any[]) {
  if (!VERBOSE) {
    return;
  }
  console.log(...content);
}

export function isCardMercenary(card: CardDef): boolean {
  return MERCENARY_CARD_DEF_IDS.has(card.defID);
}

export function isCardScavenge(card: CardDef): boolean {
  return SCAVENGE_CARD_DEF_IDS.has(card.defID);
}
export function isCardSettlement(card: CardDef): boolean {
  return SETTLEMENT_CARD_DEF_IDS.has(card.defID);
}
